import { getAuthTokenAsync } from "src/store/common";
import { OpenCall, Meta } from "src/store/types";
import * as api from "./api";

interface ApiResponse {
  data: OpenCall[];
  meta: Meta;
}

interface SingleOpenCallResponse {
  data: OpenCall;
}

interface AuditionListResponse {
  data: any[];
  meta: Meta;
}

// fetch open call list
export const fetchOpenCallList = async (data: {
  page: number;
  limit: number;
  search?: string;
  includeRoles?: boolean;
}): Promise<ApiResponse> => {
  const params: any = {
    page: data.page,
    limit: data.limit,
  };

  if (data.search && data.search.trim()) {
    params.search = data.search.trim();
  }

  if (data.includeRoles) {
    params.includeRoles = data.includeRoles;
  }

  const token = getAuthTokenAsync();
  api.setHeaderAuthorization(token);
  const response: any = await api.get(
    `${process.env.REACT_APP_ADMIN_API_URL}/open-call`,
    params,
  );
  return response;
};

// create open call
export const createOpenCall = async (
  data: any,
): Promise<SingleOpenCallResponse> => {
  const token = getAuthTokenAsync();
  api.setHeaderAuthorization(token);
  const response: any = await api.post(
    `${process.env.REACT_APP_ADMIN_API_URL}/open-call`,
    data,
  );
  return response;
};

// get open call details by ocid
export const getOpenCallDetails = async (
  ocid: string,
): Promise<SingleOpenCallResponse> => {
  const token = getAuthTokenAsync();
  api.setHeaderAuthorization(token);
  const response: any = await api.get(
    `${process.env.REACT_APP_ADMIN_API_URL}/open-call/${ocid}`,
  );
  return response;
};

// update open call
export const updateOpenCall = async (
  ocid: string,
  data: any,
): Promise<SingleOpenCallResponse> => {
  const token = getAuthTokenAsync();
  api.setHeaderAuthorization(token);
  const response: any = await api.put(
    `${process.env.REACT_APP_ADMIN_API_URL}/open-call/${ocid}`,
    data,
  );
  return response;
};

// block/unblock open call
export const blockUnblockOpenCall = async (
  ocid: string,
): Promise<SingleOpenCallResponse> => {
  const token = getAuthTokenAsync();
  api.setHeaderAuthorization(token);
  const response: any = await api.get(
    `${process.env.REACT_APP_ADMIN_API_URL}/open-call/block-unblock/${ocid}`,
  );
  return response;
};

// delete post
export const deleteOpenCall = (ocid: string) => {
  const token = getAuthTokenAsync();
  api.setHeaderAuthorization(token);
  return api.remove(`${process.env.REACT_APP_ADMIN_API_URL}/open-call/${ocid}`);
};

// Get all auditions by Open Call OCID with pagination and search
export const fetchAuditionsByOpenCall = async (data: {
  ocid: string;
  page: number;
  limit: number;
  search?: string;
  includeRoles?: boolean;
  ocrId?: string;
}): Promise<AuditionListResponse> => {
  const params: any = {
    page: data.page,
    limit: data.limit,
  };

  if (data.search && data.search.trim()) {
    params.search = data.search.trim();
  }

  if (data.includeRoles) {
    params.includeRoles = data.includeRoles;
  }

  if (data.ocrId && data.ocrId.trim()) {
    params.openCallRoleId = data.ocrId.trim();
  }

  const token = getAuthTokenAsync();
  api.setHeaderAuthorization(token);

  const response = await api.get(
    `${process.env.REACT_APP_ADMIN_API_URL}/open-call/audition/${data.ocid}`,
    params,
  );
  return response as AuditionListResponse;
};

// Block/unblock a single audition
export const blockUnblockAudition = async (pid: string) => {
  const token = getAuthTokenAsync();
  api.setHeaderAuthorization(token);
  return await api.get(
    `${process.env.REACT_APP_ADMIN_API_URL}/open-call/audition/block-unblock/${pid}`,
  );
};

// Get reported auditions with pagination and search
export const fetchReportedAuditions = async (data: {
  page: number;
  limit: number;
  search?: string;
  includeRoles?: boolean;
}): Promise<AuditionListResponse> => {
  const params: any = {
    page: data.page,
    limit: data.limit,
  };

  if (data.search && data.search.trim()) {
    params.search = data.search.trim();
  }

  if (data.includeRoles) {
    params.includeRoles = data.includeRoles;
  }

  const token = getAuthTokenAsync();
  api.setHeaderAuthorization(token);
  const response = await api.get(
    `${process.env.REACT_APP_ADMIN_API_URL}/open-call/reported-audition`,
    params,
  );
  return response as AuditionListResponse;
};

// fetch audition reports
export const fetchAuditionReports = async (pid: string) => {
  const token = getAuthTokenAsync();
  api.setHeaderAuthorization(token);
  const response: any = await api.get(
    `${process.env.REACT_APP_ADMIN_API_URL}/open-call/reported-audition/${pid}`,
  );
  return response;
};

// Delete audition
export const deleteAudition = async (pid: string) => {
  const token = getAuthTokenAsync();
  api.setHeaderAuthorization(token);
  return await api.remove(
    `${process.env.REACT_APP_ADMIN_API_URL}/open-call/audition/${pid}`,
  );
};
